import { useEffect, useId, useRef, useState } from 'react';
import { Button, Input } from '@mlm/ui';
import type { Product } from '../lib/api';
import { useProductSearch, type ProductSearchFilter } from '../lib/use-product-search';

/** The minimum a caller must know to display a chosen product honestly. */
export type ProductPickerItem = {
  id: string;
  sku: string;
  name: string;
  /** false → the selected card annotates "· archived". */
  active: boolean;
};

/**
 * Single-select product autocomplete for admin editors. Searches the catalog
 * SERVER-side (`GET /admin/products?search=`), so it is not bounded by a page
 * of pre-fetched rows the way every `<Select>` over `listProducts({ pageSize })`
 * was — the bug this replaces.
 *
 * Modelled on `member-picker.tsx`, but prefer THIS file as the template; it
 * diverges deliberately in three ways:
 *
 *  1. The result list is IN-FLOW, not an `absolute` dropdown. This renders
 *     inside dialogs whose bodies scroll (the product editor's tab body is
 *     `h-[34rem] overflow-y-auto` inside a `max-h-[90vh] overflow-y-auto`
 *     DialogContent), and an absolutely-positioned panel is clipped by them.
 *  2. Full keyboard navigation (↑/↓/Enter/Escape). This is a REPEATING ROW
 *     control — building a ten-item kit means ten interactions — so forced
 *     mouse round-trips are the difference between usable and tedious.
 *  3. Options are direct children of the `role="listbox"` element.
 *     `member-picker` nests them in `ul > li`, which breaks the listbox→option
 *     mapping for screen readers.
 *
 * Controlled, and the SELECTION IS PARENT-RESOLVED: pass the row, not an id.
 * There is no `GET /admin/products/:id` to resolve one, and the public
 * `GET /products/:id` 404s on cashier-only / activated-only products — exactly
 * the rows an admin editor exists to reach. Callers that store only an id
 * resolve it with `useProductsByIds` (see `DefaultSponsorPicker` for the same
 * shape on the member side).
 */
export function ProductPicker({
  selected,
  onChange,
  excludeIds,
  filter,
  filterResults,
  annotate,
  disabled,
  placeholder = 'Search by product name or SKU…',
}: {
  selected: ProductPickerItem | null;
  onChange: (product: ProductPickerItem | null) => void;
  /** Never selectable (e.g. the kit editing its own inclusions). */
  excludeIds?: string[];
  /** Pinned catalog filters; `search` is owned by the picker. */
  filter?: ProductSearchFilter;
  /**
   * Last-resort client-side predicate for eligibility the server cannot express
   * (e.g. `soldTo !== 'activated'`). It thins an already-ranked page, so keep it
   * NARROW — anything that would routinely reject rows belongs in `filter` as a
   * real server-side param, which is the whole point of this component.
   */
  filterResults?: (product: Product) => boolean;
  /** Optional muted note per result, e.g. "already in this kit". Non-blocking. */
  annotate?: (product: ProductPickerItem) => string | null;
  disabled?: boolean;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  // "Change" swaps the card for the search box WITHOUT emitting. Emitting null
  // there would persist a clear the admin never asked for — on a surface that
  // saves per-keystroke (the replicated page's CTA) merely opening the picker
  // and walking away would wipe the setting.
  const [editing, setEditing] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);
  const listId = useId();

  const searchable = !selected || editing;
  const { results, isLoading } = useProductSearch({
    term: query,
    filter,
    enabled: open && searchable,
  });
  const options = results.filter(
    (p) => !excludeIds?.includes(p.id) && (filterResults?.(p) ?? true),
  );

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (wrapperRef.current?.contains(e.target as Node)) return;
      setOpen(false);
      // Abandoning the search restores the existing pick rather than leaving a
      // bare input that reads as "nothing is selected".
      setEditing(false);
    }
    if (open) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // A fresh result set invalidates the highlight; keep it in range otherwise.
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // The list scrolls (`max-h-56`), so arrow-keying past the fold must follow.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, options.length]);

  function pick(p: ProductPickerItem) {
    onChange({ id: p.id, sku: p.sku, name: p.name, active: p.active });
    setQuery('');
    setOpen(false);
    setEditing(false);
  }

  if (selected && !editing) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border bg-muted/30 p-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{selected.name || '(unnamed product)'}</p>
          <p className="truncate font-mono text-xs text-muted-foreground">
            {selected.sku}
            {!selected.active && ' · archived'}
          </p>
        </div>
        <div className="flex shrink-0 items-center">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={disabled}
            onClick={() => {
              setEditing(true);
              setOpen(true);
            }}
          >
            Change
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={disabled}
            onClick={() => {
              onChange(null);
              setQuery('');
              setEditing(false);
              setOpen(false);
            }}
          >
            Clear
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="space-y-1">
      <Input
        type="text"
        role="combobox"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((i) => Math.min(i + 1, Math.max(options.length - 1, 0)));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, 0));
          } else if (e.key === 'Enter') {
            // This lives inside a <Dialog>: a bare Enter would submit or close it.
            e.preventDefault();
            const hit = options[activeIndex];
            if (hit) pick(hit);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            setOpen(false);
            setEditing(false);
          }
        }}
        disabled={disabled}
        placeholder={placeholder}
        aria-autocomplete="list"
        aria-controls={listId}
        aria-expanded={open}
        aria-activedescendant={
          open && options[activeIndex] ? `${listId}-${activeIndex}` : undefined
        }
      />
      {open && (
        <div
          id={listId}
          role="listbox"
          className="max-h-56 divide-y overflow-y-auto rounded-md border bg-popover"
        >
          {isLoading ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">Searching…</p>
          ) : options.length === 0 ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">
              {query.trim() ? 'No products match.' : 'No products available.'}
            </p>
          ) : (
            options.map((p, i) => {
              const note = annotate?.(p) ?? null;
              return (
                <button
                  key={p.id}
                  id={`${listId}-${i}`}
                  ref={i === activeIndex ? activeRef : undefined}
                  type="button"
                  role="option"
                  aria-selected={i === activeIndex}
                  className={`block w-full px-3 py-2 text-left hover:bg-muted ${
                    i === activeIndex ? 'bg-muted' : ''
                  }`}
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => pick(p)}
                >
                  <p className="truncate text-sm font-medium">{p.name}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    <span className="font-mono">{p.sku}</span>
                    {!p.active && ' · archived'}
                    {note && ` · ${note}`}
                  </p>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
